import React, { useState, useEffect, useCallback } from 'react';
import { callApi } from '../api';
import './TabakDenetim.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://klugastro-backend.steamdeneme38.workers.dev';

const TabakDenetim = () => {
  const [bekleyenler, setBekleyenler] = useState([]);
  const [onaylilar, setOnaylilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('beklemede');
  const [processingId, setProcessingId] = useState(null);
  const [mesaj, setMesaj] = useState(null);
  const [fullscreenImg, setFullscreenImg] = useState(null);

  const token = localStorage.getItem('authToken');

  const fetchBekleyenler = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/tabak/bekleyenler`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setBekleyenler(data.tabaklar || []);
    } catch (e) {
      console.error('Bekleyenler yüklenemedi:', e);
    }
  }, [token]);

  const fetchOnaylilar = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/tabak/liste?durum=onaylandi`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setOnaylilar(data.tabaklar || []);
    } catch (e) {
      console.error('Onaylılar yüklenemedi:', e);
    }
  }, [token]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchBekleyenler(), fetchOnaylilar()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchBekleyenler, fetchOnaylilar]);

  const handleDurum = async (tabakID, durum) => {
    setProcessingId(tabakID);
    try {
      const res = await fetch(`${API_BASE}/admin/tabak/durum`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tabakID, durum })
      });

      const data = await res.json();

      if (data.success) {
        setMesaj({
          type: 'success',
          text: durum === 'onaylandi' ? '✅ Tabak onaylandı!' : '❌ Tabak reddedildi.'
        });
        await Promise.all([fetchBekleyenler(), fetchOnaylilar()]);
      } else {
        setMesaj({ type: 'error', text: data.error || 'İşlem başarısız.' });
      }
    } catch (e) {
      setMesaj({ type: 'error', text: 'Bağlantı hatası.' });
    } finally {
      setProcessingId(null);
      setTimeout(() => setMesaj(null), 3000);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleString('tr-TR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return d; }
  };

  if (loading) {
    return (
      <div className="td-loading">
        <div className="td-spinner"></div>
        <p>Tabaklar yükleniyor...</p>
      </div>
    );
  }

  const activeList = activeTab === 'beklemede' ? bekleyenler : onaylilar;

  return (
    <div className="td-container">
      <div className="td-header">
        <h2 className="page-title">🍽️ Haftanın Tabağı Denetimi</h2>
        <p className="td-subtitle">Üyelerin yüklediği tabak fotoğraflarını inceleyin ve onaylayın.</p>
      </div>

      {/* Mesaj */}
      {mesaj && (
        <div className={`td-alert td-alert-${mesaj.type}`}>
          {mesaj.text}
        </div>
      )}

      {/* Tabs */}
      <div className="td-tabs">
        <button
          className={`td-tab ${activeTab === 'beklemede' ? 'td-tab-active' : ''}`}
          onClick={() => setActiveTab('beklemede')}
        >
          ⏳ Onay Bekleyen
          {bekleyenler.length > 0 && (
            <span className="td-tab-badge">{bekleyenler.length}</span>
          )}
        </button>
        <button
          className={`td-tab ${activeTab === 'onaylandi' ? 'td-tab-active' : ''}`}
          onClick={() => setActiveTab('onaylandi')}
        >
          ✅ Onaylananlar ({onaylilar.length})
        </button>
      </div>

      {/* List */}
      {activeList.length === 0 ? (
        <div className="td-empty">
          <span className="td-empty-icon">{activeTab === 'beklemede' ? '🎉' : '📋'}</span>
          <h3>{activeTab === 'beklemede' ? 'Onay bekleyen tabak yok!' : 'Henüz onaylanmış tabak yok.'}</h3>
        </div>
      ) : (
        <div className="td-grid">
          {activeList.map((tabak) => (
            <div key={tabak.id} className="td-card">
              <div
                className="td-card-img-wrap"
                onClick={() => setFullscreenImg(`${API_BASE}${tabak.gorselUrl}`)}
              >
                <img
                  src={`${API_BASE}${tabak.gorselUrl}`}
                  alt={`${tabak.uyeAd} tabağı`}
                  className="td-card-img"
                  loading="lazy"
                />
                <div className="td-card-zoom">🔍</div>
              </div>

              <div className="td-card-body">
                <div className="td-card-info">
                  <span className="td-info-label">👨‍🍳 Üye:</span>
                  <span className="td-info-value">{tabak.uyeAd || 'Anonim'}</span>
                </div>
                <div className="td-card-info">
                  <span className="td-info-label">📅 Hafta:</span>
                  <span className="td-info-value">{tabak.hafta}</span>
                </div>
                <div className="td-card-info">
                  <span className="td-info-label">🕐 Tarih:</span>
                  <span className="td-info-value">{formatDate(tabak.olusturulmaTarihi)}</span>
                </div>
                {tabak.aciklama && (
                  <div className="td-card-info">
                    <span className="td-info-label">💬</span>
                    <span className="td-info-value td-desc">{tabak.aciklama}</span>
                  </div>
                )}
                {activeTab === 'onaylandi' && (
                  <div className="td-card-info">
                    <span className="td-info-label">❤️ Oy:</span>
                    <span className="td-info-value td-vote-count">{tabak.oylar || 0}</span>
                  </div>
                )}

                {/* Onay/Red butonları sadece bekleyenler için */}
                {activeTab === 'beklemede' && (
                  <div className="td-card-actions">
                    <button
                      className="td-btn td-btn-approve"
                      onClick={() => handleDurum(tabak.id, 'onaylandi')}
                      disabled={processingId === tabak.id}
                    >
                      {processingId === tabak.id ? '⏳' : '✅'} Onayla
                    </button>
                    <button
                      className="td-btn td-btn-reject"
                      onClick={() => handleDurum(tabak.id, 'reddedildi')}
                      disabled={processingId === tabak.id}
                    >
                      {processingId === tabak.id ? '⏳' : '❌'} Reddet
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Fullscreen Görsel Modal */}
      {fullscreenImg && (
        <div className="td-modal-overlay" onClick={() => setFullscreenImg(null)}>
          <div className="td-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={fullscreenImg} alt="Tam ekran görsel" className="td-modal-img" />
            <button className="td-modal-close" onClick={() => setFullscreenImg(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabakDenetim;
